import React, { useState, useEffect, useRef } from 'react'
import styles from './MicrositeSearchBar.module.css'
import { Search, X } from 'lucide-react'

/**
 * MicrositeSearchBar
 *
 * Debounced search input used on list screens (SEO Pages, Blog,
 * Custom Pages, etc.) where typing shouldn't fire an API call on
 * every keystroke. Calls `onSearch` with the trimmed query after
 * `debounceMs` of inactivity, and immediately on Enter or clear.
 *
 * Usage:
 *   <MicrositeSearchBar
 *     placeholder="Search by title or slug…"
 *     onSearch={(query) => loadBlogPosts({ search: query })}
 *   />
 */
const MicrositeSearchBar = ({
    placeholder = 'Search…',
    onSearch,
    debounceMs = 350,
    initialValue = ''
}) => {
    const [value, setValue] = useState(initialValue)
    const timeoutRef = useRef(null)

    useEffect(() => {
        if (timeoutRef.current) clearTimeout(timeoutRef.current)
        timeoutRef.current = setTimeout(() => {
            onSearch?.(value.trim())
        }, debounceMs)

        return () => clearTimeout(timeoutRef.current)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value])

    const handleClear = () => {
        setValue('')
        onSearch?.('')
    }

    const handleKeyDown = (e) => {
        if (e.key === 'Enter') {
            if (timeoutRef.current) clearTimeout(timeoutRef.current)
            onSearch?.(value.trim())
        }
    }

    return (
        <div className={styles.wrap}>
            <Search size={14} className={styles.icon} />
            <input
                type="text"
                className={styles.input}
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder={placeholder}
            />
            {value && (
                <button type="button" className={styles.clearBtn} onClick={handleClear} aria-label="Clear search">
                    <X size={13} />
                </button>
            )}
        </div>
    )
}

export default MicrositeSearchBar